<template>
  <InfuseTable
    :columns="columns"
    :rows="rows"
    :loading="loading"
    :editable="true"
    :edit-permission-id="chargingStationTypesPermissions.UPDATE"
    :removable="true"
    :remove-permission-id="chargingStationTypesPermissions.DELETE"
    :search="{
      placeholder: t('search.placeholder')
    }"
    :has-more-items="hasMoreItems"
    @click-row="(row) => emit('clickRow', row)"
    @edit="(row) => emit('edit', row)"
    @load="emit('load')"
    @remove="(row) => emit('remove', row)"
    @search="(query) => emit('search', query)"
  >
    <template #actions>
      <InfuseButton
        data-testid="page-create-button"
        v-permission="{ id: chargingStationTypesPermissions.CREATE, hide: true }"
        class="w-64"
        size="medium"
        @click="emit('create')"
        >{{ t('action.create') }}</InfuseButton
      >
    </template>

    <template #type="{ data }">
      <span class="font-semibold">{{ data }}</span>
    </template>

    <template #evses="{ data }">
      <div class="flex items-center gap-2">
        <div v-for="evse in data" :key="`evse-${evse.identifier}`" class="flex items-center gap-2">
          <img
            v-for="connector in evse.connectors"
            :key="`connector-${connector.id}`"
            :src="iconForConnector(connector)"
            class="h-6"
            :alt="connector?.type"
          />
        </div>
      </div>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { chargingStationTypesPermissions, Connector, ConnectorType } from '@infuse/api/cpms/config';
import type { ChargingStationType } from '@infuse/cpms/config/store';

import { InfuseButton, InfuseTable } from '@infuse/ui/components';

import type { UiTableColumn } from '@infuse/ui/domain';
import { vPermission } from '@infuse/iam/permission/directives';
import { useI18n } from 'vue-i18n';

interface Emits {
  (e: 'clickRow', row: ChargingStationType): void;
  (e: 'create'): void;
  (e: 'edit', row: ChargingStationType): void;
  (e: 'load'): void;
  (e: 'remove', row: ChargingStationType): void;
  (e: 'search', query: string): void;
}

const emit = defineEmits<Emits>();

interface Props {
  rows: ChargingStationType[];
  loading?: boolean;
  page?: number;
  hasMoreItems: boolean;
}

withDefaults(defineProps<Props>(), {
  rows: () => [],
  loading: false,
  hasMoreItems: true
});

const { t } = useI18n();

const columns: UiTableColumn[] = [
  {
    name: 'type',
    label: () => t('table.header.type'),
    field: 'type',
    bold: true
  },
  {
    name: 'commercialType',
    label: () => t('table.header.commercialType'),
    field: 'commercialType'
  },
  {
    name: 'manufacturer',
    label: () => t('table.header.manufacturer'),
    field: 'manufacturer.name'
  },
  {
    name: 'evses',
    label: () => t('table.header.connectors'),
    field: 'evses'
  }
];

const iconForConnector = (connector: Connector): string => {
  let typeNamePart;
  switch (connector.connectorType) {
    case ConnectorType.C_G105:
      typeNamePart = 'CHAdeMO';
      break;
    case ConnectorType.C_CCS_2:
      typeNamePart = 'CCS';
      break;
    default:
      typeNamePart = 'TYP2';
  }

  return `/connector/${typeNamePart}/Outline/Inactive.svg`;
};
</script>

<i18n>
{
  "en": {
    "action.create": "Create Charging Station Type",
    "search.placeholder": "Search for charging station type or manufacturer",

    "table.header.type": "Type",
    "table.header.commercialType": "Commercial name",
    "table.header.manufacturer": "Manufacturer",
    "table.header.connectors": "Connectors"
  },
  "nl": {
    "action.create": "Laadpaaltype aanmaken",
    "search.placeholder": "Zoek op laadpaaltype of fabrikant",

    "table.header.type": "Type",
    "table.header.commercialType": "Commerciële naam",
    "table.header.manufacturer": "Fabrikant",
    "table.header.connectors": "Aansluitingen"
  }
}
</i18n>
